import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { FilterConfigRegistry } from "./filter-config-registry";
import { areFiltersSemanticallyEqual } from "./helpers";
import { BaseFilterInput, FilterCombinator, FilterConfig } from "./types";

export interface FilterBoxConfig<T extends BaseFilterInput> {
  registry: FilterConfigRegistry<T>;
  features: {
    search: boolean;
    combinator: boolean;
    saveAsList: boolean;
  };
  filterVisibility?: (config: FilterConfig<T>) => boolean;
  renderSaveAsListModal?: (props: {
    isOpen: boolean;
    onClose: () => void;
    onCreated?: (id: string) => void;
  }) => ReactNode;
  amplitude?: {
    onFilterClicked?: (presetType?: string, trigger?: "campaignPreset" | "manualListPreset" | "moreFilters") => void;
    onFilterApplied?: (info: {
      filterType: string;
      filterOperation: string;
      validFilterCount: number;
      combinator: string;
      filterRowId: string;
    }) => void;
    onListStarted?: (openedFrom: string, filterTypes: string[]) => void;
    onFilterTypeDropdownOpened?: (info: { filterRowId: string }) => void;
    onFilterCategorySelected?: (info: { filterRowId: string; category: string; opensSubmenu: boolean }) => void;
    onFilterSubmenuItemSelected?: (info: { filterRowId: string; category: string; filterType: string }) => void;
    onFilterOperatorDropdownOpened?: (info: { filterRowId: string; filterType: string }) => void;
    onFilterOperatorSelected?: (info: { filterRowId: string; filterType: string; operator: string }) => void;
    onFilterValueControlOpened?: (info: { filterRowId: string; filterType: string }) => void;
    onFilterValueEntered?: (info: { filterRowId: string; filterType: string }) => void;
    onFilterRowDeleted?: (info: { filterRowId: string; filterType?: string }) => void;
    onFilterCombinatorChanged?: (info: { combinator: string }) => void;
  };
}

export interface FilterBoxStateContextValue<T extends BaseFilterInput> {
  filters: T[];
  addFilter: (configId: string, options?: { skipFocus?: boolean }) => T;
  removeFilter: (filterId: string) => void;
  updateFilter: (filterId: string, updates: Partial<T>) => void;
  setFilters: (filters: T[]) => void;
  validFilters: T[];
  visibleFilters: T[];
  areAllFiltersValid: boolean;
  filterCombinator: FilterCombinator;
  setFilterCombinator: (combinator: FilterCombinator) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isInitialFilterId: (id: string) => boolean;
  hasUnsavedChanges: boolean;
  registry: FilterConfigRegistry<T>;
  features?: FilterBoxConfig<T>["features"];
  filterVisibility?: (config: FilterConfig<T>) => boolean;
  preventSaveAsList?: (filters: T[]) => boolean;
  renderSaveAsListModal?: FilterBoxConfig<T>["renderSaveAsListModal"];
  amplitude?: FilterBoxConfig<T>["amplitude"];
}

const FilterBoxStateContext = createContext<FilterBoxStateContextValue<BaseFilterInput> | null>(null);

export function useFilterBoxStateContext<T extends BaseFilterInput>(): FilterBoxStateContextValue<T> {
  const ctx = useContext(FilterBoxStateContext);
  if (!ctx) {
    throw new Error("useFilterBoxStateContext must be used within a FilterBoxStateProvider");
  }
  return ctx as unknown as FilterBoxStateContextValue<T>;
}

interface FilterBoxStateProviderProps<T extends BaseFilterInput> {
  registry: FilterConfigRegistry<T>;
  generateId?: () => string;
  initialFilters?: T[];
  onFiltersChange?: (filters: T[]) => void;
  initialFilterCombinator?: FilterCombinator;
  features?: FilterBoxConfig<T>["features"];
  filterVisibility?: (config: FilterConfig<T>) => boolean;
  preventSaveAsList?: (filters: T[]) => boolean;
  renderSaveAsListModal?: FilterBoxConfig<T>["renderSaveAsListModal"];
  amplitude?: FilterBoxConfig<T>["amplitude"];
  children: ReactNode;
}

export type { FilterBoxStateProviderProps };

export function FilterBoxStateProvider<T extends BaseFilterInput>({
  registry,
  initialFilters,
  onFiltersChange,
  initialFilterCombinator,
  features,
  filterVisibility,
  preventSaveAsList,
  renderSaveAsListModal,
  amplitude,
  children,
}: FilterBoxStateProviderProps<T>) {
  const [filters, setFilters] = useState<T[]>(initialFilters ?? []);
  const [filterCombinator, setFilterCombinator] = useState<FilterCombinator>(initialFilterCombinator ?? "AND");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const previousFiltersRef = useRef(filters);

  useEffect(() => {
    if (previousFiltersRef.current !== filters) {
      previousFiltersRef.current = filters;
      onFiltersChange?.(filters);
    }
  }, [filters, onFiltersChange]);

  const initialFilterIdsRef = useRef<Set<string>>(new Set((initialFilters ?? []).map((f) => f.id)));
  const isInitialFilterId = useCallback((id: string) => initialFilterIdsRef.current.has(id), []);

  const validFilters = useMemo(() => {
    return filters.filter((filter) => filter.isMandatory || registry.validate(filter));
  }, [filters, registry]);

  const visibleFilters = useMemo(() => {
    return filters.filter((filter) => !filter.isMandatory);
  }, [filters]);

  const areAllFiltersValid = validFilters.length === filters.length;

  const hasUnsavedChanges = useMemo(() => {
    const initial = initialFilters ?? [];
    if (filterCombinator !== initialFilterCombinator) {
      return true;
    }
    return !areFiltersSemanticallyEqual(initial, validFilters);
  }, [initialFilters, filterCombinator, initialFilterCombinator, validFilters]);

  const addFilter = useCallback(
    (configId: string, options?: { skipFocus?: boolean }) => {
      const newFilter = registry.createDefault(configId);
      if (options?.skipFocus) {
        initialFilterIdsRef.current.add(newFilter.id);
      }
      setFilters((prevFilters) => [...prevFilters, newFilter]);
      return newFilter;
    },
    [registry]
  );

  const removeFilter = useCallback((filterId: string) => {
    setFilters((prevFilters) => {
      const newFilters = prevFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length <= 1) {
        setFilterCombinator("AND");
      }
      return newFilters;
    });
  }, []);

  const updateFilter = useCallback((filterId: string, updates: Partial<T>) => {
    setFilters((prevFilters) =>
      prevFilters.map((filter) => {
        if (filter.id === filterId) {
          return {
            ...filter,
            ...updates,
          } as T;
        }
        return filter;
      })
    );
  }, []);

  const value: FilterBoxStateContextValue<T> = {
    filters,
    addFilter,
    removeFilter,
    updateFilter,
    setFilters,
    validFilters,
    visibleFilters,
    areAllFiltersValid,
    filterCombinator,
    setFilterCombinator,
    searchQuery,
    setSearchQuery,
    isInitialFilterId,
    hasUnsavedChanges,
    registry,
    features,
    filterVisibility,
    preventSaveAsList,
    renderSaveAsListModal,
    amplitude,
  };

  return (
    <FilterBoxStateContext.Provider value={value as unknown as FilterBoxStateContextValue<BaseFilterInput>}>
      {children}
    </FilterBoxStateContext.Provider>
  );
}
